import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Sitemap } from '../components/Sitemap';
import { PlayIcon, PauseIcon, VolumeIcon, Volume2Icon, MaximizeIcon, BookmarkIcon, ThumbsUpIcon, ArrowRightIcon, ArrowLeftIcon, DownloadIcon, ShareIcon, ClockIcon, TagIcon, CheckCircleIcon, XIcon } from 'lucide-react';
import { InstagramReels } from '../components/InstagramReels';
// Sample video data - would come from API in production
const VIDEO_DATA = {
  v1: {
    id: 'v1',
    title: 'English Alphabet Pronunciation',
    titleAr: 'نطق الحروف الأبجدية الإنجليزية',
    description: 'Learn how to correctly pronounce all 26 letters of the English alphabet with examples and practice.',
    descriptionAr: 'تعلم كيفية نطق جميع الحروف الأبجدية الإنجليزية الـ 26 بشكل صحيح مع أمثلة وتدريب.',
    videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-teacher-pointing-at-an-english-text-27127-large.mp4',
    thumbnail: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    duration: '8:24',
    views: 12542,
    likes: 876,
    category: 'Pronunciation',
    categoryAr: 'النطق',
    level: 'Beginner',
    levelAr: 'مبتدئ',
    tags: ['alphabet', 'pronunciation', 'beginner'],
    nextVideo: 'v2',
    prevVideo: 'v7',
    transcript: {
      en: [{
        time: '00:00',
        text: 'Welcome to our English alphabet pronunciation lesson.'
      }, {
        time: '00:10',
        text: "Today we'll learn how to correctly pronounce all 26 letters."
      }, {
        time: '00:20',
        text: "Let's start with the vowels: A, E, I, O, U."
      }, {
        time: '01:05',
        text: "Now let's move on to the consonants..."
      }],
      ar: [{
        time: '00:00',
        text: 'مرحبًا بكم في درس نطق الحروف الأبجدية الإنجليزية.'
      }, {
        time: '00:10',
        text: 'سنتعلم اليوم كيفية نطق جميع الحروف الـ 26 بشكل صحيح.'
      }, {
        time: '00:20',
        text: 'لنبدأ بالحروف المتحركة: A، E، I، O، U.'
      }, {
        time: '01:05',
        text: 'الآن لننتقل إلى الحروف الساكنة...'
      }]
    },
    relatedVideos: ['v2', 'v3']
  },
  v2: {
    id: 'v2',
    title: 'Common Greetings in English',
    titleAr: 'التحيات الشائعة باللغة الإنجليزية',
    description: 'Learn the most common English greetings and when to use them in different situations.',
    descriptionAr: 'تعلم أكثر التحيات الإنجليزية شيوعًا ومتى تستخدمها في المواقف المختلفة.',
    videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-young-woman-talking-on-video-call-with-a-friend-40837-large.mp4',
    thumbnail: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    duration: '6:15',
    views: 9876,
    likes: 743,
    category: 'Conversation',
    categoryAr: 'المحادثة',
    level: 'Beginner',
    levelAr: 'مبتدئ',
    tags: ['greetings', 'conversation', 'beginner'],
    nextVideo: 'v3',
    prevVideo: 'v1',
    transcript: {
      en: [{
        time: '00:00',
        text: 'Welcome to our lesson on common English greetings.'
      }, {
        time: '00:12',
        text: "Let's learn how to greet people in different situations."
      }, {
        time: '00:25',
        text: "First, the most common greeting: 'Hello'"
      }],
      ar: [{
        time: '00:00',
        text: 'مرحبًا بكم في درسنا حول التحيات الإنجليزية الشائعة.'
      }, {
        time: '00:12',
        text: 'دعونا نتعلم كيفية تحية الناس في المواقف المختلفة.'
      }, {
        time: '00:25',
        text: "أولاً، التحية الأكثر شيوعًا: 'Hello'"
      }]
    },
    relatedVideos: ['v1', 'v5']
  }
  // Add more videos as needed
};
export function VideoDetailPage() {
  const {
    isArabic
  } = useLanguage();
  const {
    videoId
  } = useParams<{
    videoId: string;
  }>();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showReels, setShowReels] = useState(false);
  // Get video data
  const video = videoId ? VIDEO_DATA[videoId as keyof typeof VIDEO_DATA] : null;
  // Handle video not found
  if (!video) {
    return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-4507">
        <div className="max-w-4xl mx-auto text-center" data-id="element-4508">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-4509">
            {isArabic ? 'الفيديو غير موجود' : 'Video Not Found'}
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-8" data-id="element-4510">
            {isArabic ? 'عذرًا، الفيديو الذي تبحث عنه غير موجود أو تمت إزالته.' : 'Sorry, the video you are looking for does not exist or has been removed.'}
          </p>
          <button onClick={() => navigate('/learn/video-lessons')} className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors" data-id="element-4511">
            {isArabic ? 'العودة إلى دروس الفيديو' : 'Back to Video Lessons'}
          </button>
        </div>
      </div>;
  }
  // Video control functions
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }
  };
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const seekTime = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };
  const toggleFullScreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  };
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  const handleNextVideo = () => {
    if (video.nextVideo) {
      navigate(`/learn/video-lessons/${video.nextVideo}`);
    }
  };
  const handlePrevVideo = () => {
    if (video.prevVideo) {
      navigate(`/learn/video-lessons/${video.prevVideo}`);
    }
  };
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-4512">
      <Sitemap currentPage={{
      en: video.title,
      ar: video.titleAr
    }} breadcrumbs={[{
      label: {
        en: 'Learn',
        ar: 'تعلم'
      },
      href: '/learn'
    }, {
      label: {
        en: 'Video Lessons',
        ar: 'دروس الفيديو'
      },
      href: '/learn/video-lessons'
    }]} data-id="element-4513" />
      <div className="max-w-6xl mx-auto" data-id="element-4514">
        {/* Video Player */}
        <div className="bg-black rounded-xl overflow-hidden mb-8 relative" data-id="element-4515">
          <video ref={videoRef} src={video.videoUrl} poster={video.thumbnail} className="w-full aspect-video object-contain" onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onClick={togglePlay} data-id="element-4516" />
          {/* Video Controls */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4" data-id="element-4517">
            {/* Progress Bar */}
            <div className="mb-4 flex items-center gap-2" data-id="element-4518">
              <span className="text-white text-xs" data-id="element-4519">
                {formatTime(currentTime)}
              </span>
              <div className="flex-1 relative" data-id="element-4520">
                <input type="range" min="0" max={duration || 100} value={currentTime} onChange={handleSeek} className="w-full h-1.5 bg-gray-500 rounded-full appearance-none cursor-pointer" data-id="element-4521" />
                <div className="absolute top-0 left-0 h-1.5 bg-blue-500 rounded-full pointer-events-none" style={{
                width: `${currentTime / (duration || 1) * 100}%`
              }} data-id="element-4522" />
              </div>
              <span className="text-white text-xs" data-id="element-4523">{formatTime(duration)}</span>
            </div>
            {/* Control Buttons */}
            <div className="flex items-center justify-between" data-id="element-4524">
              <div className="flex items-center gap-4" data-id="element-4525">
                <button onClick={togglePlay} className="text-white" data-id="element-4526">
                  {isPlaying ? <PauseIcon className="w-6 h-6" data-id="element-4527" /> : <PlayIcon className="w-6 h-6" data-id="element-4528" />}
                </button>
                <div className="flex items-center gap-2" data-id="element-4529">
                  <button onClick={toggleMute} className="text-white" data-id="element-4530">
                    {isMuted ? <VolumeIcon className="w-5 h-5" data-id="element-4531" /> : <Volume2Icon className="w-5 h-5" data-id="element-4532" />}
                  </button>
                  <input type="range" min="0" max="1" step="0.1" value={volume} onChange={handleVolumeChange} className="w-16 h-1 bg-gray-500 rounded-full appearance-none cursor-pointer" data-id="element-4533" />
                </div>
              </div>
              <div className="flex items-center gap-4" data-id="element-4534">
                <button onClick={() => setIsBookmarked(!isBookmarked)} className={`text-white ${isBookmarked ? 'text-yellow-400' : ''}`} data-id="element-4535">
                  <BookmarkIcon className="w-5 h-5" data-id="element-4536" />
                </button>
                <button onClick={() => setIsLiked(!isLiked)} className={`text-white ${isLiked ? 'text-red-500' : ''}`} data-id="element-4537">
                  <ThumbsUpIcon className="w-5 h-5" data-id="element-4538" />
                </button>
                <button onClick={toggleFullScreen} className="text-white" data-id="element-4539">
                  <MaximizeIcon className="w-5 h-5" data-id="element-4540" />
                </button>
              </div>
            </div>
          </div>
          {/* Play/Pause Overlay */}
          {!isPlaying && <div className="absolute inset-0 flex items-center justify-center bg-black/30" onClick={togglePlay} data-id="element-4541">
              <div className="bg-white/20 rounded-full p-4" data-id="element-4542">
                <PlayIcon className="w-12 h-12 text-white" data-id="element-4543" />
              </div>
            </div>}
        </div>
        {/* Video Info */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8" data-id="element-4544">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-4545">
            {isArabic ? video.titleAr : video.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-6" data-id="element-4546">
            <div className="flex items-center gap-1" data-id="element-4547">
              <ClockIcon className="w-4 h-4" data-id="element-4548" />
              <span data-id="element-4549">{video.duration}</span>
            </div>
            <div data-id="element-4550">{isArabic ? video.levelAr : video.level}</div>
            <div data-id="element-4551">
              {video.views.toLocaleString()} {isArabic ? 'مشاهدة' : 'views'}
            </div>
            <div data-id="element-4552">
              {video.likes.toLocaleString()} {isArabic ? 'إعجاب' : 'likes'}
            </div>
          </div>
          <p className="text-gray-700 dark:text-gray-300 mb-6" data-id="element-4553">
            {isArabic ? video.descriptionAr : video.description}
          </p>
          <div className="flex flex-wrap gap-2 mb-6" data-id="element-4554">
            {video.tags.map(tag => <div key={tag} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm" data-id="element-4555">
                #{tag}
              </div>)}
          </div>
          <div className="flex flex-wrap gap-4" data-id="element-4556">
            <button onClick={() => setShowTranscript(!showTranscript)} className="flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg" data-id="element-4557">
              <span data-id="element-4558">{isArabic ? 'النص المكتوب' : 'Transcript'}</span>
              {showTranscript ? <XIcon className="w-4 h-4" data-id="element-4559" /> : <CheckCircleIcon className="w-4 h-4" data-id="element-4560" />}
            </button>
            <button onClick={() => setShowReels(!showReels)} className="flex items-center gap-2 px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg" data-id="element-4561">
              <span data-id="element-4562">{isArabic ? 'الفيديوهات القصيرة' : 'Short Videos'}</span>
              {showReels ? <XIcon className="w-4 h-4" data-id="element-4563" /> : <CheckCircleIcon className="w-4 h-4" data-id="element-4564" />}
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg" data-id="element-4565">
              <DownloadIcon className="w-4 h-4" data-id="element-4566" />
              <span data-id="element-4567">{isArabic ? 'تحميل' : 'Download'}</span>
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg" data-id="element-4568">
              <ShareIcon className="w-4 h-4" data-id="element-4569" />
              <span data-id="element-4570">{isArabic ? 'مشاركة' : 'Share'}</span>
            </button>
          </div>
        </div>
        {/* Transcript */}
        {showTranscript && <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8" data-id="element-4571">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-4572">
              {isArabic ? 'النص المكتوب' : 'Transcript'}
            </h2>
            <div className="space-y-4" data-id="element-4573">
              {(isArabic ? video.transcript.ar : video.transcript.en).map((line, index) => <div key={index} className="flex gap-4" data-id="element-4574">
                    <div className="text-gray-500 dark:text-gray-400 whitespace-nowrap" data-id="element-4575">
                      {line.time}
                    </div>
                    <div className="text-gray-700 dark:text-gray-300" data-id="element-4576">
                      {line.text}
                    </div>
                  </div>)}
            </div>
          </div>}
        {/* Instagram Style Reels */}
        {showReels && <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden mb-8" data-id="element-4577">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700" data-id="element-4578">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-4579">
                {isArabic ? 'الفيديوهات القصيرة المتعلقة' : 'Related Short Videos'}
              </h2>
            </div>
            <InstagramReels data-id="element-4580" />
          </div>}
        {/* Navigation Buttons */}
        <div className="flex justify-between" data-id="element-4581">
          {video.prevVideo ? <button onClick={handlePrevVideo} className="flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-id="element-4582">
              <ArrowLeftIcon className="w-5 h-5" data-id="element-4583" />
              <span data-id="element-4584">{isArabic ? 'الفيديو السابق' : 'Previous Video'}</span>
            </button> : <div data-id="element-4585"></div>}
          {video.nextVideo && <button onClick={handleNextVideo} className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors" data-id="element-4586">
              <span data-id="element-4587">{isArabic ? 'الفيديو التالي' : 'Next Video'}</span>
              <ArrowRightIcon className="w-5 h-5" data-id="element-4588" />
            </button>}
        </div>
      </div>
    </div>;
}